import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, LayoutGroup, motion, useMotionValue, useReducedMotion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Monitor, Pause, Play, RotateCcw, Smartphone, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Reveal } from "@/components/flow/Reveal";
import { PrimaryButton } from "@/components/flow/Primitives";
import { WordReveal } from "@/components/flow/TextReveal";
import { EASE } from "@/lib/motion";
import { t } from "@/lib/i18n";
import { brandify, tb } from "@/lib/brandify";
import { cn } from "@/lib/utils";
import { EYEBROW, H2, SUB } from "@/pages/home/SiteChrome";
import { useBrandPreview } from "@/pages/home/BrandPreview";
import { SCENES, STORY_GROUPS as GROUPS, STORY_SCENES as FILM, StoryBeats, StoryCanvas, StoryChapterBar, StoryProvider, useStory } from "@/pages/home/HeroStage";

/* ---------------------------------------------------------------------------
   One home, start to finish — six moments, as still frames.
   No pinning, no scroll hijack. The section is one fixed-height composition:
   a list of the six How-it-works moments on the left (chips on phones) and a
   dark stage on the right that shows the *finished* screen of the chosen
   moment at once — the owner's phone or your workspace, switchable when the
   moment has both sides. While the section is on screen the moments advance
   by themselves every few seconds (thin progress line); hover pauses, a click
   takes over for good. Every frame ends on the record row it writes.
   "Watch the full flow" opens the original 60-second choreography as a film.
--------------------------------------------------------------------------- */

const MOMENTS = [
  ["address", "capture", ["phone"]],
  ["estimate", "estimate", ["phone", "desktop"]],
  ["offer", "offer", ["phone", "desktop"]],
  ["sign", "sign", ["phone", "desktop"]],
  ["onboarding", "onboard", ["phone", "desktop"]],
  ["live", "live", ["desktop", "phone"]],
].map(([id, sceneId, surfaces]) => ({ id, surfaces, scene: SCENES.find((s) => s.id === sceneId) }));
const M = MOMENTS.length;
const DWELL = 4200; // ms a moment stays while the section plays itself
const SWIPE_MIN = 44;
const N = FILM.length;

/* ---------- small hooks ---------- */
function useMediaQuery(query) {
  const [on, setOn] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const fn = () => setOn(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, [query]);
  return on;
}

/** Content-box size of an element, live. */
function useBox(ref) {
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setBox((b) => (b.w === el.clientWidth && b.h === el.clientHeight ? b : { w: el.clientWidth, h: el.clientHeight }));
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [ref]);
  return box;
}

function useInView(ref, threshold, key) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setOn(true);
      return undefined;
    }
    const io = new IntersectionObserver(([e]) => setOn(e.isIntersecting), { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold, key]);
  return on;
}

/** 0→1 over `ms` while `active`; freezes when not; restarts when `resetKey` changes. */
function useTicker(active, ms, onDone, resetKey) {
  const p = useMotionValue(0);
  useEffect(() => {
    p.set(0);
  }, [resetKey, p]);
  useEffect(() => {
    if (!active) return undefined;
    let raf = 0;
    let last = 0;
    const tick = (now) => {
      if (last) p.set(Math.min(1, p.get() + Math.min(now - last, 64) / ms));
      last = now;
      if (p.get() >= 1) {
        onDone();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, ms, onDone, p, resetKey]);
  return p;
}

const useSwipe = (onStep) => {
  const touch = useRef(null);
  const onTouchStart = (e) => {
    const p = e.touches[0];
    touch.current = { x: p.clientX, y: p.clientY, at: performance.now() };
  };
  const onTouchEnd = (e) => {
    const s = touch.current;
    touch.current = null;
    if (!s) return;
    const p = e.changedTouches[0];
    const dx = p.clientX - s.x;
    const dy = p.clientY - s.y;
    if (performance.now() - s.at > 800 || Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * 1.3) return;
    onStep(dx < 0 ? 1 : -1);
  };
  return { onTouchStart, onTouchEnd };
};

/* ================================================================== */
export function StoryScroll({ container }) {
  const wide = useMediaQuery("(min-width: 1024px)");
  const reduced = !!useReducedMotion();
  const { brand, preview } = useBrandPreview();
  const stageRef = useRef(null);
  const inView = useInView(stageRef, 0.4, wide);
  const [index, setIndex] = useState(0);
  const [pick, setPick] = useState(null);
  const [auto, setAuto] = useState(true);
  const [hover, setHover] = useState(false);
  const [film, setFilm] = useState(false);
  const moment = MOMENTS[index];
  const surface = pick && moment.surfaces.includes(pick) ? pick : moment.surfaces[0];

  const choose = useCallback((k) => {
    setIndex(((k % M) + M) % M);
    setPick(null);
    setAuto(false);
  }, []);
  const next = useCallback(() => {
    setIndex((i) => (i + 1) % M);
    setPick(null);
  }, []);
  const onSurface = useCallback((s) => {
    setPick(s);
    setAuto(false);
  }, []);
  const playing = auto && inView && !hover && !reduced && !film;
  const progress = useTicker(playing, DWELL, next, index);

  const shared = { index, moment, surface, choose, onSurface, progress, stageRef, brand: brand || undefined, reduced };
  return (
    <section id="story" className="relative mt-14 sm:mt-24 border-t border-hairline scroll-mt-16 overflow-x-clip" data-testid="story-section" data-moment={moment.id} data-surface={surface} data-playing={playing ? "true" : "false"} data-layout={wide ? "wide" : "narrow"}>
      <StoryHeader container={container} preview={preview} onPlay={() => setFilm(true)} />
      <div className={cn(container, "pb-2 sm:pb-6")}>
        {wide ? <Wide {...shared} onHover={setHover} /> : <Narrow {...shared} />}
      </div>
      <FilmDialog open={film} onOpenChange={setFilm} />
    </section>
  );
}

/* ---------- eyebrow · title · sub · play ---------- */
function StoryHeader({ container, preview, onPlay }) {
  return (
    <div className={cn(container, "pt-14 sm:pt-24")}>
      <div className="grid lg:grid-cols-12 gap-5 lg:gap-10 items-end">
        <div className="lg:col-span-7">
          <Reveal as="p" className={EYEBROW}>{t("l12.story.eyebrow")}</Reveal>
          <Reveal as="h2" delay={0.04} className={cn(H2, "max-w-[18ch]")} data-testid="story-title">{preview ? t("l12.story.title_brand", { name: preview.name }) : t("l12.story.title")}</Reveal>
        </div>
        <div className="lg:col-span-5 lg:pb-2">
          <Reveal as="p" delay={0.08} className={cn(SUB, "lg:mt-0 max-w-[52ch]")}>{preview ? tb("l12.story.sub_brand", { name: preview.name }) : tb("l12.story.sub")}</Reveal>
          <Reveal delay={0.12} className="mt-5">
            <button type="button" onClick={onPlay} className="group inline-flex h-11 items-center gap-2.5 rounded-full border border-hairline bg-surface pl-1.5 pr-4 text-[14px] font-medium text-ink transition-[border-color,background-color] duration-200 ease-editorial hover:border-ink/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/20" data-testid="story-play">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-white transition-transform duration-300 ease-editorial group-hover:scale-105"><Play className="h-3.5 w-3.5 translate-x-px" fill="currentColor" strokeWidth={0} /></span>
              {t("l15.mo.play")}
              <span className="font-mono text-[11.5px] text-ink/40 tabular-nums">{t("l15.mo.play_len")}</span>
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/* Wide: list of moments · stage                                       */
function Wide({ index, moment, surface, choose, onSurface, progress, stageRef, brand, reduced, onHover }) {
  return (
    <Reveal delay={0.1} y={16} className="mt-10 grid grid-cols-[minmax(300px,380px)_minmax(0,1fr)] xl:grid-cols-[minmax(340px,420px)_minmax(0,1fr)] gap-x-10 xl:gap-x-14 items-stretch">
      <MomentList index={index} choose={choose} progress={progress} onMouseEnter={() => onHover(true)} onMouseLeave={() => onHover(false)} />
      <Stage ref={stageRef} moment={moment} surface={surface} onSurface={onSurface} brand={brand} reduced={reduced} className="h-[560px] xl:h-[620px]" />
    </Reveal>
  );
}

function MomentList({ index, choose, progress, ...hover }) {
  const onKey = (e) => {
    const d = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : e.key === "ArrowUp" || e.key === "ArrowLeft" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    choose(index + d);
    requestAnimationFrame(() => e.currentTarget?.querySelector('[aria-selected="true"]')?.focus());
  };
  return (
    <ol role="tablist" aria-orientation="vertical" aria-label={t("l15.mo.pick")} onKeyDown={onKey} {...hover} className="flex flex-col justify-center" data-testid="story-moments">
      {MOMENTS.map((m, k) => (
        <React.Fragment key={m.id}>
          {(k === 0 || m.scene.day !== MOMENTS[k - 1].scene.day) && (
            <li role="presentation" className={cn("pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/40", k === 0 ? "pt-0" : "pt-6")} data-testid={`story-day-${m.scene.day}`}>{t("l14.st.day", { n: m.scene.day })}</li>
          )}
          <MomentItem m={m} active={k === index} onPick={() => choose(k)} progress={progress} />
        </React.Fragment>
      ))}
    </ol>
  );
}

function MomentItem({ m, active, onPick, progress }) {
  return (
    <li className="relative border-t border-hairline last:border-b">
      {/* the active item's top hairline is the progress line */}
      {active && <span aria-hidden className="absolute left-0 right-0 -top-px h-px bg-ink/10"><motion.span style={{ scaleX: progress }} className="absolute inset-0 origin-left bg-ink" data-testid="story-progress" /></span>}
      <button type="button" role="tab" aria-selected={active} aria-controls="story-stage" tabIndex={active ? 0 : -1} onClick={onPick} className="group relative flex w-full items-start gap-4 py-4 pr-3 text-left rounded-[6px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/20" data-testid={`story-moment-${m.id}`} data-active={active ? "true" : "false"}>
        <span className={cn("mt-[3px] w-[44px] shrink-0 font-mono text-[11.5px] tabular-nums transition-colors duration-300", active ? "text-ink" : "text-ink/35 group-hover:text-ink/60")}>{m.scene.clock}</span>
        <span className="min-w-0 flex-1">
          <span className={cn("block font-heading font-bold text-[17px] xl:text-[18px] tracking-[-0.02em] leading-tight transition-colors duration-300", active ? "text-ink" : "text-ink/45 group-hover:text-ink/80")}>{t(`hiw.${m.id}.t`)}</span>
          <AnimatePresence initial={false}>
            {active && (
              <motion.span key="p" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: EASE }} className="block overflow-hidden">
                <span className="block pt-2 text-[13.5px] text-ink/60 leading-[1.55] max-w-[42ch]" data-testid="story-caption">{tb(`l15.mo.${m.id}.p`)}</span>
              </motion.span>
            )}
          </AnimatePresence>
        </span>
      </button>
    </li>
  );
}

/* ================================================================== */
/* Narrow (phones, tablets): chips · stage · caption                   */
function Narrow({ index, moment, surface, choose, onSurface, progress, stageRef, brand, reduced }) {
  const rowRef = useRef(null);
  useEffect(() => {
    const row = rowRef.current;
    const el = row?.children[index];
    if (!row || !el) return;
    row.scrollTo({ left: Math.max(0, el.offsetLeft - (row.clientWidth - el.offsetWidth) / 2), behavior: reduced ? "auto" : "smooth" });
  }, [index, reduced]);
  const swipe = useSwipe((d) => {
    const k = index + d;
    if (k >= 0 && k < M) choose(k);
  });
  return (
    <div className="mt-6">
      <div ref={rowRef} role="tablist" aria-label={t("l15.mo.pick")} className="-mx-5 px-5 sm:mx-0 sm:px-0 flex gap-2 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1" data-testid="story-chips">
        {MOMENTS.map((m, k) => {
          const active = k === index;
          return (
            <button key={m.id} type="button" role="tab" aria-selected={active} onClick={() => choose(k)} className={cn("relative snap-start shrink-0 inline-flex h-9 items-center gap-2 overflow-hidden rounded-full border pl-3 pr-3.5 text-[13px] font-medium transition-[background-color,border-color,color] duration-300 ease-editorial focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/20", active ? "border-ink bg-ink text-white" : "border-hairline bg-surface text-ink/70 active:bg-fill")} data-testid={`story-chip-${m.id}`} data-active={active ? "true" : "false"}>
              <span className={cn("font-mono text-[11px] tabular-nums", active ? "text-white/60" : "text-ink/40")}>{m.scene.clock}</span>
              <span>{t(`l15.mo.${m.id}.s`)}</span>
              {active && <motion.span aria-hidden style={{ scaleX: progress }} className="absolute left-0 right-0 bottom-0 h-[2px] origin-left bg-white/70" />}
            </button>
          );
        })}
      </div>
      <Stage ref={stageRef} moment={moment} surface={surface} onSurface={onSurface} brand={brand} reduced={reduced} compact className="mt-4 -mx-5 xs:mx-0 h-[min(64dvh,600px)] min-h-[460px]" {...swipe} />
      <div className="mt-4 min-h-[96px] sm:min-h-[80px]" data-testid="story-caption-narrow">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={moment.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6, transition: { duration: 0.18 } }} transition={{ duration: 0.35, ease: EASE }}>
            <p className="font-heading font-bold text-[19px] sm:text-[21px] tracking-[-0.02em] text-ink leading-tight">{t(`hiw.${moment.id}.t`)}</p>
            <p className="mt-1.5 text-[14px] sm:text-[15px] text-ink/60 leading-[1.55] max-w-[52ch]">{tb(`l15.mo.${moment.id}.p`)}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      <p className="mt-3 text-[12px] text-ink/40 sm:hidden" data-testid="story-swipe-hint">{t("l15.mo.swipe")}</p>
    </div>
  );
}

/* ================================================================== */
/* The stage: toggle · clock · still frame · record row                */
const Stage = React.forwardRef(function Stage({ moment, surface, onSurface, brand, reduced, compact = false, className, ...rest }, ref) {
  const area = useRef(null);
  const box = useBox(area);
  const key = `${moment.id}-${surface}`;
  return (
    <div ref={ref} id="story-stage" role="tabpanel" className={cn("relative flex flex-col overflow-hidden story-stage grain grain-dark", compact ? "rounded-none xs:rounded-[24px]" : "rounded-[32px]", className)} data-testid="story-stage" data-surface={surface} data-moment={moment.id} {...rest}>
      <span aria-hidden className="pointer-events-none absolute inset-0 dot-grid-masked dot-grid-light opacity-70" />
      <div className={cn("relative z-10 flex items-center justify-between gap-3 shrink-0", compact ? "px-4 pt-4" : "px-6 pt-5")}>
        <SurfaceToggle moment={moment} surface={surface} onSurface={onSurface} compact={compact} />
        <Clock scene={moment.scene} />
      </div>
      <div className={cn("relative z-10 flex-1 min-h-0", compact ? "px-3 pt-3 pb-3" : "px-6 pt-4 pb-4")}>
        <div ref={area} className="relative h-full w-full">
          <AnimatePresence initial={false}>
            <motion.div key={key} initial={{ opacity: 0, y: 14, scale: 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.995, transition: { duration: reduced ? 0 : 0.22, ease: [0.4, 0, 1, 1] } }} transition={{ duration: reduced ? 0 : 0.45, ease: EASE }} className="absolute inset-0" data-testid="story-frame" data-frame={key}>
              <LayoutGroup id={key}>
                <StoryProvider brand={brand} scenes={[moment.scene]} index={0} playing={false} frozen surface={surface} className="h-full w-full flex items-center justify-center">
                  <StoryCanvas width={box.w} height={box.h} />
                </StoryProvider>
              </LayoutGroup>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      <RecordRow moment={moment} compact={compact} />
    </div>
  );
});

function SurfaceToggle({ moment, surface, onSurface, compact = false }) {
  const Icon = surface === "phone" ? Smartphone : Monitor;
  const label = (s) => t(`l15.mo.surface.${s}${compact ? "_s" : ""}`);
  if (moment.surfaces.length < 2) {
    return (
      <span className="inline-flex h-8 items-center gap-2 whitespace-nowrap rounded-full border border-white/10 bg-white/[0.08] pl-2.5 pr-3.5 text-[12.5px] font-medium text-white/85" data-testid="story-surface" data-surface={surface}>
        <Icon className="h-3.5 w-3.5 text-white/60" strokeWidth={1.9} />{label(surface)}
      </span>
    );
  }
  return (
    <div role="tablist" aria-label={t("l15.mo.side")} className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] p-0.5" data-testid="story-surface-toggle" data-surface={surface}>
      {["phone", "desktop"].map((s) => {
        const on = s === surface;
        const I = s === "phone" ? Smartphone : Monitor;
        return (
          <button key={s} type="button" role="tab" aria-selected={on} aria-label={t(`l15.mo.surface.${s}`)} onClick={() => onSurface(s)} className={cn("relative inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[12px] font-medium transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40", on ? "text-ink" : "text-white/60 hover:text-white")} data-testid={`story-surface-${s}`}>
            {on && <motion.span layoutId="story-surface-pill" className="absolute inset-0 rounded-full bg-white" transition={{ duration: 0.35, ease: EASE }} />}
            <I className="relative h-3.5 w-3.5" strokeWidth={2} />
            <span className="relative">{label(s)}</span>
          </button>
        );
      })}
    </div>
  );
}

/** "Day 1 · 09:02" — the moment's clock. */
function Clock({ scene, light = false, compact = false }) {
  return (
    <span className={cn("shrink-0 inline-flex items-baseline gap-1.5 font-mono tabular-nums", compact ? "text-[11px]" : "text-[12.5px]")} data-testid="story-clock" data-day={scene.day} data-time={scene.clock}>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span key={scene.day} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3, ease: EASE }} className={light ? "text-ink/40" : "text-white/45"}>{t("l14.st.day", { n: scene.day })}</motion.span>
      </AnimatePresence>
      <span className={light ? "text-ink/25" : "text-white/25"}>·</span>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span key={scene.clock} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3, ease: EASE }} className={cn("font-medium", light ? "text-ink" : "text-white")}>{scene.clock}</motion.span>
      </AnimatePresence>
    </span>
  );
}

/** What this moment writes to the property record. */
function RecordRow({ moment, compact }) {
  return (
    <div className={cn("relative z-10 shrink-0 flex items-center gap-3 border-t border-white/[0.08]", compact ? "px-4 py-3" : "px-6 py-3.5")} data-testid="story-record-row" data-field={moment.id}>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success text-white"><Check className="h-3 w-3" strokeWidth={3} /></span>
      <span className="hidden sm:inline shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white/40">{t("l15.mo.rec")}</span>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span key={moment.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3, ease: EASE }} className="min-w-0 truncate text-[12.5px] text-white/85">
          <span className="text-white/45">{t(`hiw.rec.${moment.id}`)} · </span>{t(`hiw.rec.${moment.id}_v`)}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

/* ================================================================== */
/* The film — the full 60-second choreography, on request.             */
function FilmDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="mkt w-[calc(100vw-16px)] max-w-[1120px] max-h-[calc(100dvh-16px)] overflow-y-auto gap-0 p-0 border-0 bg-canvas text-ink rounded-[24px] sm:rounded-[28px] shadow-[0_40px_120px_-40px_rgba(0,0,0,0.6)] [&>button]:hidden" aria-describedby={undefined} data-testid="story-film">
        <DialogTitle className="sr-only">{t("l15.mo.film_title")}</DialogTitle>
        {open && <Film onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function Film({ onClose }) {
  const wide = useMediaQuery("(min-width: 1024px)");
  const reduced = !!useReducedMotion();
  const { brand } = useBrandPreview();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((k) => setIndex(((k % N) + N) % N), []);
  const step = useCallback((d) => setIndex((i) => Math.max(0, Math.min(N - 1, i + d))), []);
  const swipe = useSwipe(step);
  return (
    <StoryProvider brand={brand || undefined} scenes={FILM} index={index} playing={!paused} className="story-player p-4 sm:p-6 lg:p-8" data-testid="story-film-player" data-scene={FILM[index].id} data-paused={paused ? "true" : "false"}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 pt-1"><Ruler compact={!wide} onJump={go} /></div>
        <button type="button" onClick={onClose} className="-mr-1 -mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink/60 hover:bg-fill hover:text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/20" aria-label={t("common.close")} data-testid="story-film-close"><X className="h-[18px] w-[18px]" /></button>
      </div>
      {wide ? (
        <div className="mt-5 grid grid-cols-[minmax(280px,360px)_minmax(0,1fr)] gap-x-10 h-[min(64vh,640px)]">
          <div className="min-w-0 flex flex-col justify-center pb-4"><SceneText onJump={go} /></div>
          <FilmStage />
        </div>
      ) : (
        <>
          <div className="mt-3 h-[88px] overflow-hidden" data-testid="story-text"><PlayerTitle /></div>
          <div className="story-player-stage mt-2 -mx-4 xs:mx-0" {...swipe} data-testid="story-player-stage"><FilmStage compact /></div>
          <div className="mt-3 h-[124px] sm:h-[76px] overflow-hidden" data-testid="story-caption-film"><PlayerCaption /></div>
          <div className="min-h-[8px]"><EndActions onJump={go} compact /></div>
        </>
      )}
      <PlayerTransport paused={paused} onPause={() => setPaused((p) => !p)} onStep={step} reduced={reduced} />
      <AutoAdvance enabled={!paused} onNext={() => step(1)} />
    </StoryProvider>
  );
}

function PlayerTitle() {
  const s = useStory();
  return (
    <AnimatePresence mode="wait">
      <motion.div key={s.sceneKey} exit={{ opacity: 0, y: -8, transition: { duration: 0.24, ease: [0.4, 0, 1, 1] } }}>
        <StepLabel />
        <WordReveal as="h3" text={t(`l7.h.title.${s.scene.id}`)} delay={0.04} stagger={0.045} duration={0.8} distance={12} reduced={s.reduced} play className="mt-1.5 font-display text-[24px] xs:text-[26px] sm:text-[30px] leading-[1.04] text-ink text-balance" data-testid="story-scene-title" />
      </motion.div>
    </AnimatePresence>
  );
}

function PlayerCaption() {
  const s = useStory();
  return (
    <AnimatePresence mode="wait">
      <motion.div key={s.sceneKey} exit={{ opacity: 0, y: -6, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } }}>
        <StoryBeats scene={s.scene} sceneKey={s.sceneKey} started reduced={s.reduced} stacked />
      </motion.div>
    </AnimatePresence>
  );
}

/** ‹ · pause/play · › on the left; whose screen this is on the right. 44px touch targets. */
function PlayerTransport({ paused, onPause, onStep, reduced }) {
  const s = useStory();
  const btn = "inline-flex h-11 w-11 items-center justify-center rounded-full border border-hairline bg-surface text-ink transition-[background-color,border-color,transform] duration-150 ease-editorial active:scale-95 hover:border-ink/30 disabled:opacity-35 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/25";
  return (
    <div className="mt-4 flex items-center justify-between gap-3" data-testid="story-transport">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => onStep(-1)} disabled={s.index === 0} className={btn} aria-label={t("l13.tr.prev")} data-testid="story-prev">
          <ChevronLeft className="h-[18px] w-[18px]" strokeWidth={2} />
        </button>
        {!reduced && (
          <button type="button" onClick={onPause} className={cn(btn, paused && "bg-ink border-ink text-white hover:border-ink")} aria-label={paused ? t("l13.tr.play") : t("l13.tr.pause")} aria-pressed={paused} data-testid="story-playpause" data-paused={paused ? "true" : "false"}>
            {paused ? <Play className="h-4 w-4 translate-x-px" strokeWidth={2.2} /> : <Pause className="h-4 w-4" strokeWidth={2.2} />}
          </button>
        )}
        <button type="button" onClick={() => onStep(1)} disabled={s.index === s.total - 1} className={btn} aria-label={t("l13.tr.next")} data-testid="story-next">
          <ChevronRight className="h-[18px] w-[18px]" strokeWidth={2} />
        </button>
      </div>
      <SurfaceChip light />
    </div>
  );
}

/** When a scene has played out, move on after a short hold. Holds on the last scene. */
function AutoAdvance({ enabled, onNext }) {
  const s = useStory();
  const last = s.index >= s.total - 1;
  useEffect(() => {
    if (!enabled || !s.done || s.reduced || last) return undefined;
    const id = setTimeout(onNext, 700);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, s.done, s.reduced, last, s.sceneKey]);
  return null;
}

/* The ruler — five chapters as one timeline; each chapter's width follows its running time. */
function Ruler({ compact = false, onJump }) {
  const s = useStory();
  const cols = compact ? "repeat(5, minmax(0, 1fr))" : GROUPS.map((g) => `${g.total}fr`).join(" ");
  return (
    <div className="flex items-end gap-4 sm:gap-6" data-testid="story-ruler" data-group={GROUPS[s.group].id}>
      <ol className="flex-1 min-w-0 grid gap-x-2 sm:gap-x-3" style={{ gridTemplateColumns: cols }} role="tablist" aria-label={t("l13.tr.steps")}>
        {GROUPS.map((g, k) => {
          const active = k === s.group;
          const done = k < s.group;
          return (
            <li key={g.id} className="min-w-0">
              <button type="button" role="tab" aria-selected={active} aria-label={t(`l7.h.group.${g.id}`)} onClick={() => onJump(g.firstIndex)} className={cn("group block w-full text-left focus-visible:outline-none", compact && "py-1.5 -my-1.5")} data-testid={`story-chapter-${g.id}`} data-active={active ? "true" : "false"} data-done={done ? "true" : "false"}>
                {compact ? (
                  <span className={cn("flex h-6 items-center justify-center transition-colors duration-300 ease-editorial", active ? "text-ink" : done ? "text-ink/55" : "text-ink/30")} aria-hidden><g.icon className="h-4 w-4" strokeWidth={active ? 2.2 : 1.9} /></span>
                ) : (
                  <span className={cn("block truncate text-[13px] leading-5 transition-colors duration-300 ease-editorial", active ? "text-ink font-medium" : done ? "text-ink/55 group-hover:text-ink" : "text-ink/35 group-hover:text-ink/70 group-focus-visible:text-ink/70")}>{t(`l7.h.group.${g.id}`)}</span>
                )}
                <span className={cn("relative mt-1.5 block h-[3px] rounded-full overflow-hidden transition-colors duration-300", done || active ? "bg-ink/[0.10]" : "bg-ink/[0.07] group-hover:bg-ink/[0.14]")}>
                  {done && <span className="absolute inset-0 rounded-full bg-ink/40" />}
                  {active && <StoryChapterBar key={s.sceneKey} scene={s.scene} span={g.span[s.scene.id]} clock={s.clock} reduced={s.reduced} className="bg-ink rounded-full" />}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      <span className="pb-[9px]"><Clock scene={s.scene} light compact={compact} /></span>
    </div>
  );
}

/* The words — chapter / step, the scene title word by word, then the captions in time with the screen. */
function StepLabel() {
  const s = useStory();
  const g = GROUPS[s.group];
  const Icon = g.icon;
  return (
    <motion.p initial={s.reduced ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: EASE }} className="flex items-center gap-2 text-[12px] font-medium text-ink/55 min-w-0" data-testid="story-step">
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] bg-[var(--accent-soft)] text-lavender brand-tint"><Icon className="h-3.5 w-3.5" strokeWidth={2} /></span>
      <span className="truncate">{t(`l7.h.group.${g.id}`)}</span>
      <span className="text-ink/25">/</span>
      <span className="truncate text-ink">{t(`l7.h.step.${s.scene.id}`)}</span>
      <span className="ml-auto shrink-0 font-mono text-[11px] text-ink/35 tabular-nums">{s.index + 1}/{s.total}</span>
    </motion.p>
  );
}

function SceneText({ onJump }) {
  const s = useStory();
  return (
    <div className="min-w-0" data-testid="story-text">
      <AnimatePresence mode="wait">
        <motion.div key={s.sceneKey} exit={{ opacity: 0, y: -10, transition: { duration: 0.28, ease: [0.4, 0, 1, 1] } }}>
          <StepLabel />
          <WordReveal as="h3" text={t(`l7.h.title.${s.scene.id}`)} delay={0.06} stagger={0.05} duration={0.9} distance={16} reduced={s.reduced} play className="mt-4 font-display text-[32px] xl:text-[38px] leading-[1.02] text-ink text-balance" data-testid="story-scene-title" />
          <div className="mt-5 max-w-[46ch]">
            <StoryBeats scene={s.scene} sceneKey={s.sceneKey} started reduced={s.reduced} stacked={false} />
          </div>
        </motion.div>
      </AnimatePresence>
      <EndActions onJump={onJump} />
    </div>
  );
}

/** After the last scene has played: join the waitlist, or watch again. */
function EndActions({ onJump, compact = false }) {
  const s = useStory();
  const { preview } = useBrandPreview();
  const show = s.index === s.total - 1 && s.done;
  return (
    <AnimatePresence>
      {show && (
        <motion.div key="end" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} className={cn("flex flex-wrap items-center gap-3", compact ? "mt-4" : "mt-7")} data-testid="story-end">
          <Link to={preview ? `/waitlist?brand=${preview.token}` : "/waitlist"}><PrimaryButton size={compact ? "sm" : "md"} data-testid="story-cta-start">{preview ? t("l12.bp.start_with", { name: preview.name }) : t("lp.cta_start")}</PrimaryButton></Link>
          <button type="button" onClick={() => onJump(0)} className={cn("inline-flex items-center gap-1.5 rounded-full border border-hairline bg-surface font-medium text-ink hover:border-ink/30 transition-colors", compact ? "h-10 px-3.5 text-[13px]" : "h-11 px-4 text-[14px]")} data-testid="story-replay">
            <RotateCcw className="h-4 w-4" strokeWidth={2} /> {t("l14.st.replay")}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* The film's stage — the dark cinematic panel. Chip: whose screen this is. */
function SurfaceChip({ light = false }) {
  const s = useStory();
  const Icon = s.surface === "phone" ? Smartphone : Monitor;
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span key={s.surface} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35, ease: EASE }} className={cn("inline-flex min-w-0 max-w-full items-center gap-2 rounded-full border font-medium", light ? "h-8 border-hairline bg-surface pl-2.5 pr-3 text-[11.5px] text-ink/70" : "h-8 border-white/10 bg-white/[0.08] backdrop-blur-md pl-2.5 pr-3.5 text-[12.5px] text-white/85")} data-testid="story-surface" data-surface={s.surface}>
        <Icon className={cn("h-3.5 w-3.5 shrink-0", light ? "text-ink/45" : "text-white/60")} strokeWidth={1.9} />
        <span className="truncate">{brandify(t(`l7.h.surface.${s.surface}`, { domain: s.brand.domain, brand: s.brand.name }))}</span>
      </motion.span>
    </AnimatePresence>
  );
}

function FilmStage({ compact = false }) {
  const s = useStory();
  const area = useRef(null);
  const box = useBox(area);
  return (
    <div className={cn("relative h-full w-full flex flex-col overflow-hidden story-stage grain grain-dark", compact ? "rounded-none xs:rounded-[24px]" : "rounded-[28px]")} data-testid="story-film-stage" data-surface={s.surface}>
      <span aria-hidden className="pointer-events-none absolute inset-0 dot-grid-masked dot-grid-light opacity-70" />
      {compact ? null : (
        <div className="relative z-10 flex items-center justify-between gap-3 px-6 pt-5 shrink-0">
          <SurfaceChip />
          <span className="inline-flex items-center gap-1.5 text-[11.5px] text-white/40" aria-hidden><span className="h-1.5 w-1.5 rounded-full bg-success pulse-dot" />{t("l13.stage.live")}</span>
        </div>
      )}
      <div className={cn("relative z-10 flex-1 min-h-0", compact ? "px-3 pt-4 pb-4" : "px-6 pt-4 pb-7")}>
        <div ref={area} className="relative h-full w-full flex items-center justify-center">
          <StoryCanvas width={box.w} height={box.h} />
        </div>
      </div>
    </div>
  );
}
